import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

export type JourneyTab = 'plan' | 'roadbook' | 'profile';

export interface JourneyTabLabels extends Record<JourneyTab, string> {
  sections: string;
}

export interface JourneyTabsProps {
  selected: JourneyTab;
  labels: JourneyTabLabels;
  panels: Record<JourneyTab, ReactNode>;
  onSelect: (tab: JourneyTab) => void;
}

const tabOrder: JourneyTab[] = ['plan', 'roadbook', 'profile'];

export function tabAfterFocusedRouteChange(
  current: JourneyTab,
  previousRouteKey: string | null,
  nextRouteKey: string | null
): JourneyTab {
  if (nextRouteKey === null) return 'plan';
  return nextRouteKey !== previousRouteKey ? 'profile' : current;
}

export function JourneyTabs({ selected, labels, panels, onSelect }: JourneyTabsProps) {
  const id = useId();
  const refs = useRef<Partial<Record<JourneyTab, HTMLButtonElement | null>>>({});
  const selectAndFocus = (tab: JourneyTab) => {
    onSelect(tab);
    refs.current[tab]?.focus();
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, tab: JourneyTab) => {
    const index = tabOrder.indexOf(tab);
    let next: JourneyTab | null = null;
    if (event.key === 'ArrowRight') next = tabOrder[(index + 1) % tabOrder.length];
    if (event.key === 'ArrowLeft') next = tabOrder[(index - 1 + tabOrder.length) % tabOrder.length];
    if (event.key === 'Home') next = tabOrder[0];
    if (event.key === 'End') next = tabOrder[tabOrder.length - 1];
    if (!next) return;
    event.preventDefault();
    selectAndFocus(next);
  };

  return (
    <section className="journey-tabs">
      <div className="journey-tabs__list" role="tablist" aria-label={labels.sections}>
        {tabOrder.map((tab) => (
          <button
            key={tab}
            ref={(node) => { refs.current[tab] = node; }}
            id={`${id}-${tab}-tab`}
            type="button"
            role="tab"
            aria-selected={selected === tab}
            aria-controls={`${id}-${tab}-panel`}
            tabIndex={selected === tab ? 0 : -1}
            onClick={() => onSelect(tab)}
            onKeyDown={(event) => handleKeyDown(event, tab)}
          >
            {labels[tab]}
          </button>
        ))}
      </div>
      <div
        id={`${id}-${selected}-panel`}
        className="journey-tabs__panel"
        role="tabpanel"
        aria-labelledby={`${id}-${selected}-tab`}
        tabIndex={0}
      >
        {panels[selected]}
      </div>
    </section>
  );
}
